import React, { Component } from "react";
import translate from "../../constants/translations";
import StartConversation from "./start-conversation";
import {
	getCultureCode,
	sendMessageAllowed,
} from "../../api/secure-messaging-api";
import events from "../../constants/events";
import cultureCodes from "../../constants/culture-codes";
import type { CustomerContext } from "../../types";

const contactDetailsLoadedEventName = "wp-secure-messaging:item-loaded";
const contactDetailsSavedEventName = "wp-secure-messaging:item-save-success";
const contactDetailsChangedEventName = "wp-secure-messaging:item-changed";

const isEmpty = (text: string | null | undefined) => !text || text.length === 0;

interface ContactDetailsLoadedEventDetail {
	email?: string;
	mobile?: string;
}

interface ContactDetailsChangedEventDetail {
	valid: boolean;
}

interface ContactDetailsElement extends HTMLElement {
	submit: () => void;
}

interface EnsureContactDetailsAndStartConversationProps {
	customer: CustomerContext;
}

interface EnsureContactDetailsAndStartConversationState {
	cultureCode: string;
	contactDetailsMissing: boolean;
	contactDetailsComponentIsReady: boolean;
	contactDetailsValid: boolean;
	sendAllowed: boolean;
}

class EnsureContactDetailsAndStartConversation extends Component<
	EnsureContactDetailsAndStartConversationProps,
	EnsureContactDetailsAndStartConversationState
> {
	private mounted = false;

	private contactDetails = React.createRef<ContactDetailsElement>();

	private onContactDetailsLoaded: EventListener;

	private onContactDetailsSaved: EventListener;

	private onContactDetailsChanged: EventListener;

	constructor(props: EnsureContactDetailsAndStartConversationProps) {
		super(props);

		this.onSaveContactDetailsClick = this.onSaveContactDetailsClick.bind(this);
		this.onCancelClick = this.onCancelClick.bind(this);

		this.onContactDetailsLoaded = (event) => {
			const e = event as CustomEvent<ContactDetailsLoadedEventDetail>;
			this.setState({
				contactDetailsMissing:
					isEmpty(e.detail?.email) && isEmpty(e.detail?.mobile),
				contactDetailsComponentIsReady: true,
			});
		};

		this.onContactDetailsSaved = () => {
			this.setState({ contactDetailsMissing: false });
		};

		this.onContactDetailsChanged = (event) => {
			const e = event as CustomEvent<ContactDetailsChangedEventDetail>;
			this.setState({ contactDetailsValid: !!e.detail?.valid });
		};

		this.state = {
			cultureCode: props.customer.cultureCode || "en-GB",
			contactDetailsMissing: true,
			contactDetailsComponentIsReady: false,
			contactDetailsValid: false,
			sendAllowed: false,
		};
	}

	componentDidMount() {
		const { customer } = this.props;
		const contactDetailsElement = this.contactDetails.current;
		this.mounted = true;

		getCultureCode(customer).then((cultureCode) => {
			if (this.mounted && cultureCode) {
				this.setState({ cultureCode });
			}
		});

		sendMessageAllowed(customer).then((sendAllowed) => {
			if (this.mounted) {
				this.setState({ sendAllowed });
			}
		});

		if (contactDetailsElement) {
			contactDetailsElement.addEventListener(
				contactDetailsLoadedEventName,
				this.onContactDetailsLoaded,
			);
			contactDetailsElement.addEventListener(
				contactDetailsSavedEventName,
				this.onContactDetailsSaved,
			);
			contactDetailsElement.addEventListener(
				contactDetailsChangedEventName,
				this.onContactDetailsChanged,
			);
		}
	}

	componentWillUnmount() {
		const contactDetailsElement = this.contactDetails.current;

		this.mounted = false;

		if (contactDetailsElement) {
			contactDetailsElement.removeEventListener(
				contactDetailsLoadedEventName,
				this.onContactDetailsLoaded,
			);
			contactDetailsElement.removeEventListener(
				contactDetailsSavedEventName,
				this.onContactDetailsSaved,
			);
			contactDetailsElement.removeEventListener(
				contactDetailsChangedEventName,
				this.onContactDetailsChanged,
			);
		}
	}

	private t(term: string): string {
		return translate(this.state.cultureCode, term);
	}

	private onSaveContactDetailsClick() {
		const contactDetailsElement = this.contactDetails.current;
		contactDetailsElement?.submit();
	}

	private onCancelClick() {
		window.dispatchEvent(new Event(events.newMessageSentOrCancelled));
	}

	private getAuthorizationAttributes(): Record<string, string> {
		const { customer } = this.props;

		if (
			customer.waypointBffRoutes !== null &&
			customer.waypointBffRoutes !== undefined &&
			customer.waypointBffRoutes !== ""
		) {
			return {
				"is-bff": "true",
				"bff-route-prefixes": customer.waypointBffRoutes,
			};
		}

		return {
			"token-key": customer.tokenKey || "",
		};
	}

	private disabled(): boolean {
		const { sendAllowed, contactDetailsValid } = this.state;
		return !sendAllowed && !contactDetailsValid;
	}

	private showContactDetailsForm(
		contactDetailsMissing: boolean,
		cultureCode: string,
	): boolean {
		return (
			contactDetailsMissing &&
			cultureCode !== cultureCodes.norwegian &&
			cultureCode !== cultureCodes.finnish &&
			cultureCode !== cultureCodes.swedishFinland
		);
	}

	render() {
		const { customer } = this.props;
		const {
			sendAllowed,
			cultureCode,
			contactDetailsMissing,
			contactDetailsComponentIsReady,
		} = this.state;

		const contactDetailsStyle = {
			display: contactDetailsComponentIsReady ? "block" : "none",
		};

		return this.showContactDetailsForm(contactDetailsMissing, cultureCode) ? (
			<div style={contactDetailsStyle}>
				<div className="if row">
					<div className="if col-10--sm">
						<h5 className="if heading">{this.t("ContactDetailsHeader")}</h5>
						<p>{this.t("ContactDetailsInformationText")}</p>
					</div>
				</div>

				<wp-secure-messaging
					ref={this.contactDetails}
					edit
					customer-id={customer.personId || ""}
					locale={cultureCode}
					{...this.getAuthorizationAttributes()}
				/>

				<div className="if u-margin-top--40">
					<button
						disabled={this.disabled()}
						id="saveContactDetails"
						type="button"
						onClick={this.onSaveContactDetailsClick}
						className="if button primary"
					>
						{this.t("SaveContactDetailsButtonText")}
					</button>
					<button
						id="cancel"
						type="button"
						onClick={this.onCancelClick}
						className="if button tertiary"
					>
						{this.t("CancelSending")}
					</button>
				</div>
			</div>
		) : (
			<StartConversation
				customer={customer}
				cultureCode={cultureCode}
				sendAllowed={sendAllowed}
				category={customer.category || undefined}
			/>
		);
	}
}

export default EnsureContactDetailsAndStartConversation;
